import { useState, useRef, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, X, Check, TrendingUp, Lightbulb, BarChart3, Zap } from 'lucide-react';
import { caseStudies } from '@/data/caseStudiesData';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

/* ─── Metric Card ─── */
function MetricCard({ value, label }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-30px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6 }}
      className="bg-secondary border border-border p-8 text-center text-foreground"
      data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="font-serif text-4xl sm:text-5xl font-medium tracking-tight block mb-2">{value}</span>
      <span className="text-sm tracking-widest uppercase text-muted-foreground">{label}</span>
    </motion.div>
  );
}

/* ─── Photo Slideshow (images only with prev/next) ─── */
function PhotoSlideshow({ images, client, onOpenLightbox }) {
  const [idx, setIdx] = useState(0);
  const total = images.length;

  if (total === 0) return null;

  return (
    <div className="max-w-5xl mx-auto" data-testid="slideshow-gallery">
      <div className="relative bg-secondary mb-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="cursor-pointer"
            onClick={() => onOpenLightbox(idx)}
          >
            <img
              src={images[idx]}
              alt={`${client} — ${idx + 1}`}
              className="w-full h-[60vh] md:h-[70vh] object-contain bg-white mx-auto"
              style={{ objectPosition: 'center top' }}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={() => setIdx((p) => (p - 1 + total) % total)} className="flex items-center gap-2 text-muted-foreground hover:text-butter transition-colors" data-testid="slideshow-prev">
          <ArrowLeft className="h-5 w-5" /> Previous
        </button>
        <span className="text-sm text-muted-foreground font-medium tracking-widest">{idx + 1} / {total}</span>
        <button onClick={() => setIdx((p) => (p + 1) % total)} className="flex items-center gap-2 text-muted-foreground hover:text-butter transition-colors" data-testid="slideshow-next">
          Next <ArrowRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

/* ─── Video Gallery (separate grid of playable videos) ─── */
function VideoGallery({ videos }) {
  if (!videos || videos.length === 0) return null;

  return (
    <div className="max-w-5xl mx-auto" data-testid="video-gallery">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.map((vid, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="relative bg-black rounded-sm overflow-hidden"
            style={{ aspectRatio: '9/16' }}
          >
            <video
              className="w-full h-full object-contain"
              controls
              playsInline
              preload="metadata"
              src={`${vid}#t=0.5`}
            >
              <source src={vid} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ─── Performance KPI Card ─── */
function PerformanceKPI({ value, label, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-30px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="p-8 border border-border text-center hover:border-butter/50 transition-colors duration-300"
    >
      <span className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight block mb-2">{value}</span>
      <span className="text-xs tracking-widest uppercase text-muted-foreground">{label}</span>
    </motion.div>
  );
}

/* ─── Insight Card ─── */
function InsightCard({ text, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-30px' });
  const icons = [TrendingUp, Lightbulb, BarChart3, Zap, TrendingUp, Lightbulb];
  const Icon = icons[index % icons.length];
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 15 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="flex items-start gap-4 p-6 bg-secondary border border-border"
    >
      <div className="w-9 h-9 rounded-full bg-butter/20 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="h-4 w-4 text-foreground" />
      </div>
      <p className="text-sm leading-relaxed">{text}</p>
    </motion.div>
  );
}

/* ─── Result Item (extracted to avoid hook-in-loop) ─── */
function ResultItem({ result, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-30px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -20 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="flex items-start gap-4 p-6 bg-secondary border border-border"
      data-testid={`result-${index}`}
    >
      <div className="w-8 h-8 rounded-full bg-butter flex items-center justify-center flex-shrink-0 mt-0.5">
        <Check className="h-4 w-4 text-foreground" />
      </div>
      <p className="text-sm md:text-base leading-relaxed">{result}</p>
    </motion.div>
  );
}

export default function CaseStudyDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const study = caseStudies.find((s) => s.slug === slug);
  const currentIdx = caseStudies.findIndex((s) => s.slug === slug);
  const nextStudy = caseStudies[(currentIdx + 1) % caseStudies.length];

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const allImages = study ? [study.heroImage, ...(study.galleryImages || [])] : [];

  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowRight') setLightboxIdx((p) => (p + 1) % allImages.length);
      if (e.key === 'ArrowLeft') setLightboxIdx((p) => (p - 1 + allImages.length) % allImages.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen, allImages.length]);

  if (!study) {
    return (
      <div className="pt-44 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Case study not found.</p>
          <Link to="/case-studies" className="text-sm underline hover:text-butter-dark">Back to Case Studies</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32" data-testid={`case-study-detail-${slug}`}>

      {/* ─── HERO ─── */}
      <section ref={heroRef} className="relative min-h-[70vh] flex items-end overflow-hidden bg-foreground" data-testid="csd-hero">
        <motion.div style={{ scale: heroScale }} className="absolute inset-0 z-0">
          <img src={study.heroImage} alt={study.client} className="w-full h-full object-contain object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/40 to-foreground/20" />
        </motion.div>
        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 container-main pb-16 md:pb-24">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeInUp} className="mb-4">
              <Link to="/case-studies" className="text-background/60 text-sm hover:text-background transition-colors">
                &larr; All Case Studies
              </Link>
            </motion.div>
            <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-xs tracking-widest uppercase text-background/60">{study.industry}</span>
              <span className="w-1 h-1 rounded-full bg-background/40" />
              <span className="text-xs tracking-widest uppercase text-background/60">{study.category[0]}</span>
            </motion.div>
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl lg:text-7xl font-medium text-background leading-[1.08] mb-4">
              {study.client}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-background/70 text-base md:text-lg max-w-xl">
              {study.tagline}
            </motion.p>
          </motion.div>
        </motion.div>
      </section>

      {/* ─── OVERVIEW ─── */}
      <section className="py-24 md:py-32" data-testid="csd-overview">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 max-w-6xl mx-auto">
            <div className="lg:col-span-8">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
                <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Overview</motion.p>
                <motion.h2 variants={fadeInUp} className="font-serif text-2xl sm:text-3xl font-medium leading-snug mb-8">
                  {study.overview}
                </motion.h2>
              </motion.div>
            </div>
            <div className="lg:col-span-4">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-8">
                <motion.div variants={fadeInUp}>
                  <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Services</p>
                  <div className="flex flex-wrap gap-2">
                    {study.services.map((s) => (
                      <span key={s} className="text-xs border border-border px-3 py-1.5 rounded-full text-foreground">{s}</span>
                    ))}
                  </div>
                </motion.div>
                <motion.div variants={fadeInUp}>
                  <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Industry</p>
                  <p className="text-sm font-medium">{study.industry}</p>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ─── CHALLENGE & STRATEGY ─── */}
      <section className="py-24 md:py-32" data-testid="csd-challenge-strategy">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-5xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">The Challenge</motion.p>
              <motion.p variants={fadeInUp} className="text-base md:text-lg leading-relaxed text-foreground">{study.challenge}</motion.p>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">The Strategy</motion.p>
              <motion.p variants={fadeInUp} className="text-base md:text-lg leading-relaxed text-foreground">{study.strategy}</motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── METRICS (if available) ─── */}
      {study.metrics && study.metrics.length > 0 && (
        <>
          <div className="border-t border-border" />
          <section className="py-24 md:py-32 bg-foreground text-background" data-testid="csd-metrics">
            <div className="container-main">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
                <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-background/50 mb-4">Performance</motion.p>
                <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl font-medium text-background">Results That Speak</motion.h2>
              </motion.div>
              <div className={`grid gap-6 max-w-4xl mx-auto ${study.metrics.length === 1 ? 'grid-cols-1' : study.metrics.length === 2 ? 'grid-cols-1 md:grid-cols-2' : study.metrics.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4'}`}>
                {study.metrics.map((m) => (
                  <MetricCard key={m.label} {...m} />
                ))}
              </div>
              {study.globalMarkets && (
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="mt-12 text-center">
                  <p className="text-xs tracking-widest uppercase text-background/50 mb-4">Global Creator Network</p>
                  <div className="flex items-center justify-center gap-6 flex-wrap">
                    {study.globalMarkets.map((m) => (
                      <span key={m} className="text-background/80 font-medium text-lg">{m}</span>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </section>
        </>
      )}

      {/* ─── RESULTS ─── */}
      <section className={`py-24 md:py-32 ${study.metrics?.length ? '' : 'border-t border-border'}`} data-testid="csd-results">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Deliverables & Results</motion.p>
              <motion.h2 variants={fadeInUp} className="font-serif text-2xl sm:text-3xl font-medium leading-snug mb-12">
                What We Achieved
              </motion.h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {study.results.map((result, i) => (
                <ResultItem key={i} result={result} index={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── PERFORMANCE HIGHLIGHTS ─── */}
      {study.performanceHighlights && (
        <>
          <div className="border-t border-border" />
          <section className="py-24 md:py-32" data-testid="csd-performance">
            <div className="container-main">
              <div className="max-w-5xl mx-auto">
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16">
                  <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Performance Highlights</motion.p>
                  <motion.h2 variants={fadeInUp} className="font-serif text-2xl sm:text-3xl font-medium leading-snug">
                    Organic impact, measured.
                  </motion.h2>
                </motion.div>

                {/* KPI Cards */}
                {study.metrics && study.metrics.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                    {study.metrics.map((m, i) => (
                      <PerformanceKPI key={m.label} value={m.value} label={m.label} index={i} />
                    ))}
                  </div>
                )}
                {study.slug === 'its-a-10-haircare' && (
                  <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-muted-foreground text-sm text-center mb-20 max-w-2xl mx-auto">
                    Organic social performance achieved through strategic content planning, creative direction, campaign execution, and ongoing community engagement.
                  </motion.p>
                )}
                {study.slug === 'rewind-it-10' && (
                  <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-muted-foreground text-sm text-center mb-20 max-w-2xl mx-auto">
                    Organic social growth driven through educational storytelling, product demonstrations, creator-focused content, and strategic campaign execution.
                  </motion.p>
                )}

                {/* Key Insights */}
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-20">
                  <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-8">Key Insights</motion.p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {study.performanceHighlights.insights.map((insight, i) => (
                      <InsightCard key={i} text={insight} index={i} />
                    ))}
                  </div>
                </motion.div>

                {/* Top Performing Content */}
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-20">
                  <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-8">Top Performing Content</motion.p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {study.performanceHighlights.topContent.map((item, i) => (
                      <motion.div
                        key={item}
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.06 }}
                        className="p-5 border border-border text-center hover:border-butter/50 hover:bg-butter/5 transition-all duration-300"
                      >
                        <span className="text-sm font-medium">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Services Provided */}
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
                  <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-8">Services Provided</motion.p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4">
                    {study.services.map((service, i) => (
                      <motion.div
                        key={service}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.04 }}
                        className="flex items-center gap-3 py-2 border-b border-border/50"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-butter flex-shrink-0" />
                        <span className="text-sm text-foreground">{service}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ─── PHOTO GALLERY ─── */}
      {allImages.length > 1 && (
        <>
          <div className="border-t border-border" />
          <section className="py-24 md:py-32" data-testid="csd-gallery">
            <div className="container-main">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-12">
                <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Gallery</motion.p>
                <motion.h2 variants={fadeInUp} className="font-serif text-2xl sm:text-3xl font-medium">Selected Work</motion.h2>
              </motion.div>

              <PhotoSlideshow images={allImages} client={study.client} onOpenLightbox={(i) => { setLightboxIdx(i); setLightboxOpen(true); }} />

              {/* Thumbnail strip */}
              <div className="flex gap-2 mt-8 justify-center flex-wrap">
                {allImages.map((img, i) => (
                  <button
                    key={`img-${i}`}
                    onClick={() => { setLightboxIdx(i); setLightboxOpen(true); }}
                    className="w-16 h-16 overflow-hidden border border-border hover:border-butter transition-colors duration-200"
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ─── VIDEO GALLERY ─── */}
      {study.videos && study.videos.length > 0 && (
        <>
          <div className="border-t border-border" />
          <section className="py-24 md:py-32" data-testid="csd-videos">
            <div className="container-main">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-12">
                <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Video Content</motion.p>
                <motion.h2 variants={fadeInUp} className="font-serif text-2xl sm:text-3xl font-medium">In Motion</motion.h2>
              </motion.div>

              <VideoGallery videos={study.videos} />
            </div>
          </section>
        </>
      )}

      {/* ─── NEXT PROJECT ─── */}
      <section className="py-24 md:py-32 bg-foreground" data-testid="csd-next-project">
        <div className="container-main">
          <Link
            to={`/case-studies/${nextStudy.slug}`}
            className="group block max-w-4xl mx-auto text-center"
            data-testid="next-project-link"
          >
            <p className="text-xs tracking-widest uppercase text-background/40 mb-4">Next Project</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl font-medium text-background group-hover:text-butter transition-colors duration-500 mb-4">
              {nextStudy.client}
            </h2>
            <p className="text-background/50 text-sm mb-8">{nextStudy.tagline}</p>
            <span className="inline-flex items-center gap-2 bg-butter text-foreground px-8 py-3 rounded-full text-xs tracking-widest uppercase font-medium group-hover:bg-butter-dark transition-colors duration-300">
              View More
            </span>
          </Link>
        </div>
      </section>

      {/* ─── LIGHTBOX ─── */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            <button onClick={() => setLightboxOpen(false)} className="absolute top-6 right-6 text-white/60 hover:text-white z-50">
              <X className="h-8 w-8" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIdx((p) => (p - 1 + allImages.length) % allImages.length); }}
              className="absolute left-6 text-white/60 hover:text-white z-50"
            >
              <ArrowLeft className="h-8 w-8" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIdx((p) => (p + 1) % allImages.length); }}
              className="absolute right-6 text-white/60 hover:text-white z-50"
            >
              <ArrowRight className="h-8 w-8" />
            </button>
            <img
              src={allImages[lightboxIdx]}
              alt={`${study.client} — ${lightboxIdx + 1}`}
              className="max-w-[90vw] max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <span className="absolute bottom-6 text-white/40 text-sm tracking-widest">
              {lightboxIdx + 1} / {allImages.length}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
